import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { PayPalCheckout } from "@/components/PayPalCheckout";
import { LEGAL_NOTICE } from "@/config";

const gbp = (n) => `£${n.toFixed(2)}`;

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, clearCart } = useCart();
  const shipping = subtotal >= 150 || subtotal === 0 ? 0 : 6.99;
  const total = subtotal + shipping;
  const [agree, setAgree] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", address: "", city: "", postcode: "", notes: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-28 text-center">
        <h1 className="font-display text-2xl font-bold text-slate-900">Your cart is empty</h1>
        <p className="mt-3 text-slate-600">Add some research resources before checking out.</p>
        <Link to="/shop"><Button className="mt-6 btn-metallic font-semibold">Explore Research</Button></Link>
      </div>
    );
  }

  const validate = () => {
    const required = ["full_name", "email", "address", "city", "postcode"];
    if (required.some((k) => !form[k].trim())) { toast.error("Please complete all required fields."); return false; }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) { toast.error("Please enter a valid email."); return false; }
    if (!agree) { toast.error("Please accept the lawful-use agreement to continue."); return false; }
    return true;
  };

  const buildPayload = () => ({
    ...form,
    country: "United Kingdom",
    items: items.map((i) => ({ id: i.id, name: i.name, type: i.type, price: i.price, qty: i.qty })),
  });

  const onPaid = (orderId) => {
    clearCart();
    navigate(`/order/${orderId}`, { replace: true });
  };

  return (
    <div className="pt-8 pb-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> Continue shopping</Link>
        <h1 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Checkout</h1>

        <form onSubmit={(e) => e.preventDefault()} className="mt-10 grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-3 space-y-6">
            <div className="rounded-2xl glass-strong metallic-border p-6 sm:p-8">
              <h2 className="font-display text-lg font-semibold text-slate-900">Delivery details</h2>
              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2"><Label className="text-slate-700">Full name *</Label><Input data-testid="checkout-name" value={form.full_name} onChange={update("full_name")} className="bg-white border-slate-200" /></div>
                <div className="space-y-2"><Label className="text-slate-700">Email *</Label><Input data-testid="checkout-email" type="email" value={form.email} onChange={update("email")} className="bg-white border-slate-200" /></div>
                <div className="space-y-2"><Label className="text-slate-700">Phone</Label><Input data-testid="checkout-phone" value={form.phone} onChange={update("phone")} className="bg-white border-slate-200" /></div>
                <div className="space-y-2 sm:col-span-2"><Label className="text-slate-700">Address *</Label><Input data-testid="checkout-address" value={form.address} onChange={update("address")} className="bg-white border-slate-200" /></div>
                <div className="space-y-2"><Label className="text-slate-700">City *</Label><Input data-testid="checkout-city" value={form.city} onChange={update("city")} className="bg-white border-slate-200" /></div>
                <div className="space-y-2"><Label className="text-slate-700">Postcode *</Label><Input data-testid="checkout-postcode" value={form.postcode} onChange={update("postcode")} className="bg-white border-slate-200" /></div>
                <div className="space-y-2 sm:col-span-2"><Label className="text-slate-700">Order notes</Label><Textarea data-testid="checkout-notes" value={form.notes} onChange={update("notes")} rows={3} className="bg-white border-slate-200 resize-none" /></div>
              </div>
            </div>

            <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5">
              <label className="flex cursor-pointer items-start gap-3">
                <Checkbox data-testid="checkout-agree" checked={agree} onCheckedChange={setAgree} className="mt-0.5 border-amber-500/50 data-[state=checked]:bg-amber-500 data-[state=checked]:text-black" />
                <span className="text-xs leading-relaxed text-amber-900"><span className="font-semibold text-amber-700">Lawful-use agreement: </span>{LEGAL_NOTICE} I confirm my intended use is lawful in my jurisdiction.</span>
              </label>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="sticky top-24 rounded-2xl glass-strong metallic-border p-6">
              <h2 className="font-display text-lg font-semibold text-slate-900">Order summary</h2>
              <div className="mt-5 space-y-3">
                {items.map((i) => (
                  <div key={i.key} className="flex gap-3">
                    <img src={i.image} alt={i.name} className="h-12 w-12 rounded-md object-cover" />
                    <div className="flex flex-1 justify-between">
                      <div><p className="text-sm text-slate-800">{i.name}</p><p className="text-xs text-slate-500">Qty {i.qty}</p></div>
                      <span className="text-sm text-slate-800">{gbp(i.price * i.qty)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 space-y-2 border-t border-slate-200 pt-5 text-sm">
                <div className="flex justify-between text-slate-600"><span>Subtotal</span><span className="text-slate-800">{gbp(subtotal)}</span></div>
                <div className="flex justify-between text-slate-600"><span>Shipping</span><span className="text-slate-800">{shipping === 0 ? "Free" : gbp(shipping)}</span></div>
                <div className="flex justify-between border-t border-slate-200 pt-3 text-base font-semibold"><span className="text-slate-900">Total</span><span className="metallic-text">{gbp(total)}</span></div>
              </div>
              <div className="mt-6">
                <PayPalCheckout validate={validate} buildPayload={buildPayload} onPaid={onPaid} amount={total} />
              </div>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-slate-500"><ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> Pay with PayPal or card · UK dispatch</p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
